import { DispatchOnly } from "../../../../telemetry/DispatchOnly";
import { Measures, addMarkerToPageTimings } from "../../../../telemetry/Measures";
import { Trace } from "../../../../telemetry/Trace";
import { dashboard } from "../../../../telemetry/TraceLogging";
import { Stack, useTheme } from "@fluentui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAepPayloadContentType, getSettings, setSettings } from "../../../externalApi/AepApi";
import { getSignInStatus } from "../../../externalApi/AuthenticatorApi";
import { clearLaunchAttributionContext } from "../../../externalApi/DashboardHostApi";
import { getDashboardHostApiInternal } from "../../../externalApi/DashboardHostImpl";
import { isFeatureEnabled } from "../../../externalApi/VelocityApi";
import { addVisibilityEventListeners, getIsWindowVisible, getLastShowReason, isPinWidgetsFlyout, removeVisibilityEventListeners, setPinWidgetsFlyout } from "../../../externalApi/WindowApi";
import { tryGetHostApi, } from "../../../externalApi/externalApi";
import { addWidgetOrchestratorEventListener, getFeedsInstancePromise, removeWidgetOrchestratorEventListener, } from "../../../webUXApi/widgetApi";
import { FeedContainer } from "../../FeedContainer/FeedContainer";
import { AppType, PageType } from "../../../constants";
import { lazyLoadModule } from "../../../lazyLoadModule";
import { tryParseJson } from "../../../utils/stringUtilities";
import { WidgetPickerInteraction } from "../../../utils/widgetDialogUtilities";
import { getWidgetOrchestrator } from "../../../widgetRuntime/orchestrator/orchestrator";
import { getWidgetComponentApiInstance } from "../../../widgetRuntime/widgetApis/_widgetComponentApiInstance";
import { FullWidgetContainer } from "../../FullWidgetWithErrorContainer/FullWidgetWithErrorContainer";
import { WidgetGroup } from "../../WidgetGroup/WidgetGroup";
import { logPageEvent, logPageHidden, logPageShown } from "../Telemetry/PageTelemetry";
import { bootstrapPrefetchTask } from "./Prefetch";
const DashboardSettingsDialog = React.lazy(() => import(
/* webpackChunkName: "DashboardSettingsDialog-Chunk" */
"../../DashboardSettingsDialog/DashboardSettingsDialog"));
const EvolvedSettingsDialog = React.lazy(() => import(
/* webpackChunkName: "EvolvedSettingsDialog-Chunk" */
"../../EvolvedSettingsDialog/EvolvedSettingsDialog"));
const WidgetPickerDialog = React.lazy(() => import(
/* webpackChunkName: "WidgetPickerDialog-Chunk" */
"../../WidgetPickerDialog/WidgetPickerDialog"));
const TelemetryModule = import(
/* webpackChunkName: "Telemetry-Chunk" */
"../../../../telemetry/Telemetry");
const MainPage = (props) => {
    addMarkerToPageTimings("MEE.MainPageStart");
    const theme = useTheme();
    const [isWidgetPickerDialogShown, setIsWidgetPickerDialogShown] = useState(false);
    const [showArgs, setShowArgs] = useState("");
    const [isDashboardSettingsDialogShown, setIsDashboardSettingsDialogShown] = useState(false);
    const [isDMADashboardSettingsDialogShown, setIsDMADashboardSettingsDialogShown] = useState(false);
    const [showContent, setShowContent] = useState(true);
    const [isBadgingEnabled, setIsBadgingEnabled] = useState(false);
    const [isHoverEnabled, setIsHoverEnabled] = useState(false);
    const [isRotationEnabled, setIsRotationEnabled] = useState(false);
    const [isPinWidgetsFlyoutEnabled, setPinWidgetsFlyoutEnabled] = useState(false);
    const [evolvedSettingsPageSettings, setEvolvedSettingsPageSettings] = useState({
        isAboutExpanded: true,
        isPersonalizeExpanded: true,
    });
    const [personalizeToShow, setPersonalizeToShow] = useState("");
    async function updateSettingsButtons(isOpen) {
        if (!isOpen) {
            const settingsJson = await getSettings();
            // JSON return in form of { object : array [ objects ] } requiring parsing in this form.
            const settingsJSON = tryParseJson(settingsJson);
            if (settingsJSON != null) {
                for (const setting in settingsJSON.Settings) {
                    if ("BadgingEnabled" in settingsJSON.Settings[setting]) {
                        setIsBadgingEnabled(settingsJSON.Settings[setting].BadgingEnabled);
                    }
                    else if ("HoverEnabled" in settingsJSON.Settings[setting]) {
                        setIsHoverEnabled(settingsJSON.Settings[setting].HoverEnabled);
                    }
                    else if ("RotationEnabled" in settingsJSON.Settings[setting]) {
                        setIsRotationEnabled(settingsJSON.Settings[setting].RotationEnabled);
                    }
                }
            }
            else {
                setIsBadgingEnabled(true);
                setIsHoverEnabled(true);
                setIsRotationEnabled(true);
            }
            const PinWidgetsFlyoutEnabled = await isPinWidgetsFlyout();
            setPinWidgetsFlyoutEnabled(PinWidgetsFlyoutEnabled);
        }
        else {
            setSettings(isHoverEnabled, isBadgingEnabled, isRotationEnabled);
        }
    }
    function showDialog(launchSource) {
        //0 if profile pic, 1 if add widgets button
        if (launchSource === 0) {
            if (!isFeatureEnabled("Feature_SettingsFeedCustomization")) {
                updateSettingsButtons(false);
            }
            setIsDMADashboardSettingsDialogShown(true);
            setIsDashboardSettingsDialogShown(true);
            Measures.logEvent(dashboard, "DashboardSettingsLaunched", 33554432 /* productAndServiceUsage */, {
                isDynamicFeedEnabled: true,
                launchSource: launchSource,
            });
        }
        else if (launchSource === 1) {
            showPickerDialog(1 /* AddButtonClicked */);
        }
    }
    async function onWidgetPicked(widgetDefinitionId, listIndex, searchBoxInteracted) {
        let orchestrator = await getWidgetOrchestrator();
        let registrations = await orchestrator.getWidgetRegistrations();
        let pickedRegistration = null;
        for (let registration of registrations) {
            if (registration.id === widgetDefinitionId) {
                pickedRegistration = registration;
            }
        }
        if (pickedRegistration === null) {
            return;
        }
        logWidgetPickerInteraction(pickedRegistration, listIndex, searchBoxInteracted);
        await orchestrator.addWidgetInstance(pickedRegistration);
        return Promise.resolve();
    }
    function logWidgetPickerInteraction(pickedRegistration, listIndex, searchBoxInteracted) {
        // TODO: https://task.ms/43725494 provide the correct value for isFeatured
        let partCPayload = {
            addedWidgetDefinitionId: pickedRegistration.id,
            appId: pickedRegistration.appId,
            change: WidgetPickerInteraction.AddWidget,
            index: listIndex,
            isFeatured: false,
            providerId: pickedRegistration.providerId,
            searchBoxInteracted: (searchBoxInteracted === 1),
        };
        Measures.logEvent(dashboard, "WidgetPickerInteracted", 33554432 /* productAndServiceUsage */, partCPayload);
        DispatchOnly.logEvent(dashboard, "WidgetPickerInteracted", 1 /* diagnosticData */, 33554432 /* productAndServiceUsage */, partCPayload);
    }
    function showPickerDialog(launchSource) {
        const isXamlWidgetPickerEnabled = isFeatureEnabled("Feature_XamlWidgetPicker");
        Measures.logEvent(dashboard, "AddWidgetsLaunched", 33554432 /* productAndServiceUsage */, {
            isDynamicFeedEnabled: true,
            launchSource: launchSource,
        });
        if (!isXamlWidgetPickerEnabled) {
            setIsWidgetPickerDialogShown(true);
        }
        else {
            tryGetHostApi("WidgetPicker", 1, 1).then((widgetPickerApi) => {
                if (widgetPickerApi && widgetPickerApi.showPicker) {
                    widgetPickerApi.showPicker(async (widgetDefinitionId, listIndex, searchBoxInteracted) => {
                        await onWidgetPicked(widgetDefinitionId, listIndex, searchBoxInteracted);
                        setFocusToRootContainer();
                    }).then((shouldShowFallbackPicker) => {
                        if (shouldShowFallbackPicker === 1) {
                            setIsWidgetPickerDialogShown(true);
                        }
                    });
                }
                else {
                    setIsWidgetPickerDialogShown(true);
                }
            });
        }
    }
    function onDashboardSettingsDialogDismiss() {
        if (isDashboardSettingsDialogShown) {
            if (!isFeatureEnabled("Feature_SettingsFeedCustomization")) {
                updateSettingsButtons(true);
            }
            lazyLoadModule(TelemetryModule).then((Telemetry) => {
                Telemetry.logEvent(dashboard, "DashboardSettingsDismissed", 16777216 /* productAndServicePerformance */, {});
            });
        }
        setShowArgs("");
        setPersonalizeToShow("");
        setIsDMADashboardSettingsDialogShown(false);
        setIsDashboardSettingsDialogShown(false);
        setFocusToRootContainer();
    }
    function onWidgetPickerDialogDismiss() {
        if (isWidgetPickerDialogShown) {
            lazyLoadModule(TelemetryModule).then((Telemetry) => {
                Telemetry.logEvent(dashboard, "AddWidgetsDismissed", 16777216 /* productAndServicePerformance */, {});
            });
            DispatchOnly.logEvent(dashboard, "AddWidgetsDismissed", 0 /* requiredServiceData */, 16777216 /* productAndServicePerformance */, {});
        }
        setShowArgs("");
        setIsWidgetPickerDialogShown(false);
    }
    function setFocusToRootContainer() {
        const rootContainer = document.getElementById("root-container");
        if (rootContainer) {
            rootContainer.focus();
        }
    }
    function onBadgingToggle() {
        setSettings(isHoverEnabled, !isBadgingEnabled, isRotationEnabled);
        setIsBadgingEnabled(!isBadgingEnabled);
    }
    function onHoverToggle() {
        setSettings(!isHoverEnabled, isBadgingEnabled, isRotationEnabled);
        setIsHoverEnabled(!isHoverEnabled);
    }
    function onRotationToggle() {
        setSettings(isHoverEnabled, isBadgingEnabled, !isRotationEnabled);
        setIsRotationEnabled(!isRotationEnabled);
    }
    function onPinToggle() {
        setPinWidgetsFlyout(!isPinWidgetsFlyoutEnabled);
        setPinWidgetsFlyoutEnabled(!isPinWidgetsFlyoutEnabled);
    }
    function onWidgetOrchestratorEvent(name, widgetInstance) {
        Trace.logEvent(dashboard, "WebAppMainPageOrchestratorEvent", 0 /* verbose */, {
            appId: widgetInstance === null || widgetInstance === void 0 ? void 0 : widgetInstance.widgetRegistration.appId,
            name: name,
            providerId: widgetInstance === null || widgetInstance === void 0 ? void 0 : widgetInstance.widgetRegistration.providerId,
            widgetDefintionId: widgetInstance === null || widgetInstance === void 0 ? void 0 : widgetInstance.widgetRegistration.id,
            widgetInstanceId: widgetInstance === null || widgetInstance === void 0 ? void 0 : widgetInstance.widgetId,
        });
    }
    async function getFeedTelemetryPromise() {
        let result = await getFeedsInstancePromise().then((feedInstance) => {
            if (feedInstance === undefined) {
                Measures.logEvent(dashboard, "FailedToRetrieveFeedInstance", 16777216 /* productAndServicePerformance */);
            }
            let feedTelemetry = {
                appId: feedInstance === undefined ? "undefined" : feedInstance.widgetRegistration.appId,
                indexedDBInstanceId: feedInstance === undefined ? "undefined" : feedInstance.instanceId.toString(),
                instanceId: feedInstance === undefined ? "undefined" : feedInstance.instanceId.toString(),
                percentVisible: feedInstance === undefined ? "Unknown" : feedInstance.telemetry.percentVisible,
                providerId: feedInstance === undefined ? "undefined" : feedInstance.widgetRegistration.providerId,
                state: feedInstance === undefined ? "undefined" : feedInstance.widgetState,
                stateErrorCode: feedInstance === undefined ? 0 : feedInstance.widgetErrorCode,
                stateErrorReason: feedInstance === undefined ? "undefined" : feedInstance.widgetErrorStateReason,
                visibility: feedInstance === undefined ? 0 /* Unknown */ : feedInstance.telemetry.visibility,
                widgetDefinitionId: feedInstance === undefined ? "undefined" : feedInstance.widgetDefinitionId,
                widgetInstanceId: feedInstance === undefined ? "undefined" : feedInstance.widgetInstanceId,
            };
            return feedTelemetry;
        });
        return result;
    }
    useEffect(() => {
        const widgetInstanceApi = getWidgetComponentApiInstance();
        if (widgetInstanceApi) {
            widgetInstanceApi.addEventListener("triggeraction", showDialog);
        }
        return () => {
            if (widgetInstanceApi) {
                widgetInstanceApi.removeEventListener("triggeraction", showDialog);
            }
        };
    }, [showDialog]);
    const themeRef = useRef(theme);
    useEffect(() => {
        themeRef.current = theme;
        const widgetInstanceApi = getWidgetComponentApiInstance();
        if (widgetInstanceApi) {
            widgetInstanceApi.updateTheme(theme);
        }
    }, [theme]);
    useEffect(() => {
        const widgetInstanceApi = getWidgetComponentApiInstance();
        const responseThemeRequest = () => {
            widgetInstanceApi.updateTheme(themeRef.current);
        };
        if (widgetInstanceApi) {
            widgetInstanceApi.addEventListener("gettheme", responseThemeRequest);
        }
        return () => {
            if (widgetInstanceApi) {
                widgetInstanceApi.removeEventListener("gettheme", responseThemeRequest);
            }
        };
    }, []);
    useEffect(() => {
        window.addEventListener("dashboardscroll", onDashboardScrollEvent, { passive: true });
        return () => {
            window.removeEventListener("dashboardscroll", onDashboardScrollEvent);
        };
    }, []);
    function onDashboardScrollEvent() {
        Measures.logEvent(dashboard, "WebAppScrollOccurred", 33554432 /* productAndServiceUsage */);
    }
    useEffect(() => {
        addWidgetOrchestratorEventListener(onWidgetOrchestratorEvent);
        return function cleanup() {
            removeWidgetOrchestratorEventListener(onWidgetOrchestratorEvent);
        };
    }, []);
    function scrollWidgetsContainerToView() {
        const widgetContainer = document.getElementById("widget-containers");
        if (widgetContainer) {
            widgetContainer.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
            lazyLoadModule(TelemetryModule).then((Telemetry) => {
                Telemetry.logEvent(dashboard, "ScrollToWidgetsOnBackgroundRefresh", 16777216 /* productAndServicePerformance */);
            });
        }
    }
    function handleIntersection(entries) {
        entries.forEach((entry) => {
            if (entry.target.id === "feed-container") {
                if (entry.isIntersecting) {
                    Measures.logEvent(dashboard, "FeedContainerShown", 16777216 /* productAndServicePerformance */);
                }
            }
        });
    }
    const handleWindowHidden = async () => {
        onDashboardSettingsDialogDismiss();
        onWidgetPickerDialogDismiss();
        if (props.isAvoidRenderWhileHiddenEnabled) {
            setShowContent(false);
        }
        const [aepPayloadContentType, lastShowReason] = await Promise.all([
            getAepPayloadContentType(),
            getLastShowReason(),
        ]);
        getFeedTelemetryPromise().then((feedTelemetry) => {
            let webxtwidgetList;
            const widgetModelApiFactory = window.__com_microsoft_dsh_widgetModelApiFactory;
            if (widgetModelApiFactory) {
                const signalApi = widgetModelApiFactory.tryGetApi("signals", 1, 1);
                if (signalApi) {
                    webxtwidgetList = signalApi.getWebxtWidgetListForTelemetry();
                }
            }
            let isColdLaunch = feedTelemetry.state === "unknown" || feedTelemetry.state === "loading";
            let data = {
                aepPayloadContentType: aepPayloadContentType,
                feedInfo: feedTelemetry,
                isDynamicFeedEnabled: true,
                lastShowReason: lastShowReason,
                launchType: isColdLaunch ? "cold" : "warm",
                webxtwidgetList: webxtwidgetList,
            };
            logPageHidden("main", data);
        });
    };
    const handleWindowShown = async (args) => {
        Measures.logEvent(dashboard, "MainPageWindowShownEntry", 16777216 /* productAndServicePerformance */, {
            isAvoidRenderWhileHiddenEnabled: props.isAvoidRenderWhileHiddenEnabled
        });
        clearLaunchAttributionContext();
        if (props.isAvoidRenderWhileHiddenEnabled) {
            setShowContent(true);
        }
        const [aepPayloadContentType, lastShowReason, signinStatus] = await Promise.all([
            getAepPayloadContentType(),
            getLastShowReason(),
            getSignInStatus()
        ]);
        const isSignedIn = signinStatus !== 2 /* SignedOut */;
        getFeedTelemetryPromise().then((feedTelemetry) => {
            let webxtwidgetList;
            let boardView;
            const widgetModelApiFactory = window.__com_microsoft_dsh_widgetModelApiFactory;
            if (widgetModelApiFactory) {
                const signalApi = widgetModelApiFactory.tryGetApi("signals", 1, 1);
                if (signalApi) {
                    webxtwidgetList = signalApi.getWebxtWidgetListForTelemetry();
                }
                const feedModelApi = widgetModelApiFactory.tryGetApi("feedsmodel", 1, 1);
                if (feedModelApi) {
                    boardView = feedModelApi.getSelectedFeedPivot();
                }
            }
            // Best guess: if the feed is unknown\loading during a window show, Widgets is probably in a cold state
            let isColdLaunch = feedTelemetry.state === "unknown" || feedTelemetry.state === "loading";
            let data = {
                args: args,
                boardView: boardView,
                feedInfo: feedTelemetry,
                isDynamicFeedEnabled: true,
                isReauthRequiredUserEnabled: true,
                isSignedIn: isSignedIn,
                isSignedOutUserEnabled: true,
                launchType: isColdLaunch ? "cold" : "warm",
                webxtwidgetList: webxtwidgetList,
            };
            logPageShown("main", aepPayloadContentType, lastShowReason, data);
            if (args.length > 0) {
                // protocol launch logic
                Trace.logEvent(dashboard, "WebAppProtocolLaunchInvoked", 0 /* verbose */, {
                    args: args,
                });
                let lowerCaseArgs = args.toLocaleLowerCase();
                if (lowerCaseArgs.startsWith("picker")) {
                    // picker protocol launch, ms-widgets://picker?widgetDefinitionId=<definationId>
                    setShowArgs(args);
                    showPickerDialog(2 /* UriLaunch */);
                }
                else if (lowerCaseArgs.startsWith("setting-personalize")) {
                    // feed personalize setting protocol launch, ms-widgets://setting-personalize?feedProviderId=<providerId>
                    setIsDashboardSettingsDialogShown(true);
                    setIsDMADashboardSettingsDialogShown(true);
                    // receiving feed provider Id from args
                    if (args.includes("feedProviderId")) {
                        let argsArray = args.split("=");
                        if (argsArray.length === 2) {
                            let feedProviderId = argsArray[1];
                            if (feedProviderId.length > 0) {
                                setPersonalizeToShow(feedProviderId);
                            }
                        }
                    }
                }
            }
            if (isColdLaunch) {
                let dashboardHostApi = getDashboardHostApiInternal();
                let cachedSessionId = "";
                if (dashboardHostApi) {
                    // sessionId normally gets logged from the hostapp, but due to the delay it may be overridden
                    // cache the current sessionId to work around the timing issue
                    cachedSessionId = dashboardHostApi.sessionId;
                }
                // Feed is not ready and cannot receive the dispatch event for cold launch
                // Wait a bit and send the dispatch event directly
                setTimeout(() => {
                    getFeedTelemetryPromise().then((updatedFeedInfo) => {
                        if (updatedFeedInfo.state === "normal") {
                            Trace.logEvent(dashboard, "DelayedEventForFeed", 1 /* information */, {
                                Event: "WebAppLandingPageShown",
                                cachedSessionId: cachedSessionId,
                                isReauthRequiredUserEnabled: true,
                                isSignedIn: isSignedIn,
                                isSignedOutUserEnabled: true,
                                lastShowReason: lastShowReason,
                            });
                            if (widgetModelApiFactory) {
                                const signalApi = widgetModelApiFactory.tryGetApi("signals", 1, 1);
                                if (signalApi) {
                                    webxtwidgetList = signalApi.getWebxtWidgetListForTelemetry();
                                }
                                const feedModelApi = widgetModelApiFactory.tryGetApi("feedsmodel", 1, 1);
                                if (feedModelApi) {
                                    boardView = feedModelApi.getSelectedFeedPivot();
                                }
                            }
                            let eventData = {
                                boardView: boardView,
                                cachedSessionId: cachedSessionId,
                                feedInfo: feedTelemetry,
                                isDynamicFeedEnabled: true,
                                isReauthRequiredUserEnabled: true,
                                isSignedIn: isSignedIn,
                                isSignedOutUserEnabled: true,
                                launchType: "cold",
                                webxtwidgetList: webxtwidgetList,
                            };
                            logPageShown("main", aepPayloadContentType, lastShowReason, eventData);
                        }
                        else {
                            Measures.logEvent(dashboard, "FeedNeverBecameReady", 16777216 /* productAndServicePerformance */, {
                                Page: "main",
                                cachedSessionId: cachedSessionId,
                                isReauthRequiredUserEnabled: true,
                                isSignedIn: isSignedIn,
                                isSignedOutUserEnabled: true,
                                lastShowReason: lastShowReason,
                                updatedFeedInfo: updatedFeedInfo,
                            });
                        }
                    });
                }, 15000); // 90th percentile cold launch is ~13 seconds
            }
        });
    };
    //Adding Intersection Obeservers to watch for visibility of Add Widgets Button and Feed Container.
    //Callback sets Jump to News button to be shown if both Add Widgets Button and Feed Container are not visible.
    useEffect(() => {
        logPageEvent("main");
        const addWidgetsButton = document.getElementById("add-widgets-button");
        const observer = new IntersectionObserver(handleIntersection, {
            root: null,
            rootMargin: "0px",
            threshold: 0,
        });
        if (addWidgetsButton) {
            observer.observe(addWidgetsButton);
        }
        const feedContainer = document.getElementById("feed-container");
        if (feedContainer) {
            observer.observe(feedContainer);
        }
        addVisibilityEventListeners(handleWindowShown, handleWindowHidden);
        // If the Window is visible fire the visibility event since we will have missed the first on on cold launch.
        getIsWindowVisible().then((visible) => {
            if (visible) {
                handleWindowShown(""); // No args since this is an internal event
            }
        });
        if (props.setResetMainPageCallback) {
            props.setResetMainPageCallback(scrollWidgetsContainerToView);
        }
        return () => {
            if (addWidgetsButton) {
                observer.unobserve(addWidgetsButton);
            }
            if (feedContainer) {
                observer.unobserve(feedContainer);
            }
            removeVisibilityEventListeners(handleWindowShown, handleWindowHidden);
            if (props.setResetMainPageCallback) {
                props.setResetMainPageCallback(undefined);
            }
        };
    }, []);
    useEffect(() => {
        lazyLoadModule(TelemetryModule).then((Telemetry) => {
            Telemetry.logEvent(dashboard, "WebAppPerfMeasure", 16777216 /* productAndServicePerformance */, {
                type: "MainPageRendered"
            });
        });
    }, []);
    useMemo(() => {
        // Bootstrap prefetch task to save the feed request
        bootstrapPrefetchTask();
    }, []);
    // This ref is used to pass the HTMLElement for the resize and settings buttons into WidgetContainer to be used in keyboarding logic.
    const feedContainerRef = useRef(null);
    return (React.createElement(React.Fragment, null,
        React.createElement(FullWidgetContainer, { showContent: showContent, onClick: props.onClick },
            React.createElement(Stack, { id: "widget-containers" },
                React.createElement(WidgetGroup, { appType: AppType, pageType: PageType, pcsAdditionalQsp: "" })),
            React.createElement(Stack, { id: "feed-container", style: { scrollMarginTop: "25px" } },
                React.createElement(FeedContainer, { ref: feedContainerRef }))),
        isDashboardSettingsDialogShown && (React.createElement(React.Suspense, { fallback: React.createElement("div", null) }, isFeatureEnabled("Feature_SettingsFeedCustomization") ?
            React.createElement(EvolvedSettingsDialog, { isOpen: isDMADashboardSettingsDialogShown, onDismiss: onDashboardSettingsDialogDismiss, showPickerDialog: showPickerDialog, setEvolvedSettingsPageSettings: setEvolvedSettingsPageSettings, settingsPageSettings: evolvedSettingsPageSettings, personalizeToShow: personalizeToShow })
            :
                React.createElement(DashboardSettingsDialog, { isOpen: isDashboardSettingsDialogShown, showArgs: showArgs, onDismiss: onDashboardSettingsDialogDismiss, onBadgingToggle: onBadgingToggle, onHoverToggle: onHoverToggle, onRotationToggle: onRotationToggle, onPinToggle: onPinToggle, isBadgingEnabled: isBadgingEnabled, isHoverEnabled: isHoverEnabled, isRotationEnabled: isRotationEnabled, isPinWidgetsFlyoutEnabled: isPinWidgetsFlyoutEnabled }))),
        isWidgetPickerDialogShown && (React.createElement(React.Suspense, { fallback: React.createElement("div", null) },
            React.createElement(WidgetPickerDialog, { isOpen: isWidgetPickerDialogShown, showArgs: showArgs, onDismiss: onWidgetPickerDialogDismiss })))));
};
export default MainPage;
